import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Phone,
  Globe,
  Search,
  Filter,
  X,
  ExternalLink,
  Heart,
  Brain,
  Users,
  Shield,
  Calendar,
  FileText,
  Home,
  LifeBuoy,
  Info,
  ChevronRight
} from 'lucide-react';
import { Modal } from '../common/Modal';

interface Resource {
  id: string;
  name: string;
  description: string;
  website: string;
  phone?: string;
  category: 'life-management' | 'death-management' | 'support' | 'crisis' | 'professional';
  icon: React.ReactNode;
  color: string;
  textColor: string;
  additionalInfo?: string;
}

const resources: Resource[] = [
  // Crisis Support Resources
  {
    id: 'lifeline',
    name: 'Lifeline',
    description: '24/7 crisis support and suicide prevention services. Available via phone, text, or online chat. Confidential and non-judgmental support for anyone experiencing emotional distress.',
    website: 'https://www.lifeline.org.au/',
    phone: '13 11 14',
    category: 'crisis',
    icon: <Phone size={24} />,
    color: 'bg-red-50',
    textColor: 'text-red-600',
    additionalInfo: 'Lifeline provides 24/7 crisis support and suicide prevention services. You can call, text, or chat online. All conversations are confidential and free.'
  },
  {
    id: '1800yarn',
    name: '1800YARN',
    description: '24/7 culturally safe crisis support line for Aboriginal and Torres Strait Islander peoples, staffed by Aboriginal and Torres Strait Islander Crisis Supporters.',
    website: 'https://www.13yarn.org.au/',
    phone: '1800 927 764',
    category: 'crisis',
    icon: <LifeBuoy size={24} />,
    color: 'bg-teal-50',
    textColor: 'text-teal-600',
    additionalInfo: '1800YARN offers confidential, culturally responsive crisis support provided by Aboriginal and Torres Strait Islander Crisis Supporters, ensuring community members can yarn with someone who understands.'
  },
  {
    id: 'beyond-blue',
    name: 'Beyond Blue',
    description: 'Australia\'s most well-known mental health organization. Get support for anxiety, depression, and suicide prevention. Provides information, support, and referrals.',
    website: 'https://www.beyondblue.org.au/',
    phone: '1300 22 4636',
    category: 'support',
    icon: <Heart size={24} />,
    color: 'bg-blue-50',
    textColor: 'text-blue-600',
    additionalInfo: 'Beyond Blue provides information and support to help everyone in Australia achieve their best possible mental health, whatever their age and wherever they live.'
  },
  {
    id: 'kids-helpline',
    name: 'Kids Helpline',
    description: '24/7 counselling for young people aged 5-25. Free, confidential, and anonymous support via phone, email, or web chat.',
    website: 'https://kidshelpline.com.au/',
    phone: '1800 55 1800',
    category: 'support',
    icon: <Users size={24} />,
    color: 'bg-purple-50',
    textColor: 'text-purple-600',
    additionalInfo: 'Kids Helpline is Australia\'s only free, confidential 24/7 online and phone counselling service for young people aged 5 to 25.'
  },
  {
    id: '1800respect',
    name: '1800RESPECT',
    description: '24/7 national sexual assault, domestic and family violence counselling service. Free, confidential support for anyone affected by sexual assault or domestic violence.',
    website: 'https://www.1800respect.org.au/',
    phone: '1800 737 732',
    category: 'support',
    icon: <Shield size={24} />,
    color: 'bg-pink-50',
    textColor: 'text-pink-600',
    additionalInfo: '1800RESPECT is the national sexual assault, domestic and family violence counselling service. It\'s a free, confidential 24/7 service.'
  },
  {
    id: 'mensline',
    name: 'MensLine Australia',
    description: 'Telephone and online support, information and referral service for men with family and relationship concerns. Available 24/7.',
    website: 'https://mensline.org.au/',
    phone: '1300 78 99 78',
    category: 'support',
    icon: <Users size={24} />,
    color: 'bg-blue-50',
    textColor: 'text-blue-600',
    additionalInfo: 'MensLine Australia is a professional telephone and online support and information service for Australian men.'
  },
  {
    id: 'headspace',
    name: 'Headspace',
    description: 'National youth mental health foundation providing support and services to young people aged 12-25. Offers online and face-to-face support.',
    website: 'https://headspace.org.au/',
    phone: '1800 650 890',
    category: 'support',
    icon: <Brain size={24} />,
    color: 'bg-green-50',
    textColor: 'text-green-600',
    additionalInfo: 'Headspace provides early intervention mental health services to 12-25 year olds, along with assistance in promoting young peoples\' wellbeing.'
  },
  // Life Management Resources
  {
    id: 'myagedcare',
    name: 'My Aged Care',
    description: 'Australian Government website providing information and access to aged care services. Helps you find and access the right aged care services for your needs.',
    website: 'https://www.myagedcare.gov.au/',
    phone: '1800 200 422',
    category: 'life-management',
    icon: <Home size={24} />,
    color: 'bg-amber-50',
    textColor: 'text-amber-600',
    additionalInfo: 'My Aged Care is the starting point to access Australian Government-funded aged care services. The website and phone line can help you find services.'
  },
  {
    id: 'carers-australia',
    name: 'Carers Australia',
    description: 'National peak body representing Australia\'s carers. Provides information, support, and advocacy for people who care for family members or friends.',
    website: 'https://www.carersaustralia.com.au/',
    phone: '1800 242 636',
    category: 'life-management',
    icon: <Heart size={24} />,
    color: 'bg-orange-50',
    textColor: 'text-orange-600',
    additionalInfo: 'Carers Australia is the national peak body representing Australia\'s carers, advocating on their behalf to influence policies and services.'
  },
  {
    id: 'centrelink',
    name: 'Centrelink',
    description: 'Australian Government service providing financial support and services. Access payments, services, and support for various life situations.',
    website: 'https://www.servicesaustralia.gov.au/centrelink',
    phone: '132 307',
    category: 'life-management',
    icon: <FileText size={24} />,
    color: 'bg-indigo-50',
    textColor: 'text-indigo-600',
    additionalInfo: 'Centrelink delivers social security payments and services to Australians. You can access services online, by phone, or in person.'
  },
  {
    id: 'ndis',
    name: 'NDIS (National Disability Insurance Scheme)',
    description: 'Supports people with permanent and significant disability. Provides funding for supports and services to help achieve goals and participate in the community.',
    website: 'https://www.ndis.gov.au/',
    phone: '1800 800 110',
    category: 'life-management',
    icon: <LifeBuoy size={24} />,
    color: 'bg-teal-50',
    textColor: 'text-teal-600',
    additionalInfo: 'The NDIS provides funding to eligible people with disability to gain more time with family and friends, greater independence, access to new skills, jobs, or volunteering in their community.'
  },
  {
    id: 'seniors-card',
    name: 'Seniors Card',
    description: 'Government-issued card providing discounts and benefits for seniors. Available in most Australian states and territories.',
    website: 'https://www.seniorscard.com.au/',
    category: 'life-management',
    icon: <Calendar size={24} />,
    color: 'bg-cyan-50',
    textColor: 'text-cyan-600',
    additionalInfo: 'Seniors Cards provide discounts and benefits for eligible seniors. Each state and territory has its own program with different benefits.'
  },
  // Death Management Resources
  {
    id: 'funeral-directors',
    name: 'Australian Funeral Directors Association',
    description: 'Professional association representing funeral directors across Australia. Provides information and helps you find accredited funeral service providers.',
    website: 'https://www.afda.org.au/',
    phone: '1300 888 188',
    category: 'death-management',
    icon: <Heart size={24} />,
    color: 'bg-gray-50',
    textColor: 'text-gray-600',
    additionalInfo: 'The AFDA represents funeral directors across Australia and provides information to help you make informed decisions about funeral services.'
  },
  {
    id: 'griefline',
    name: 'Griefline',
    description: 'Free telephone and online counselling service for people experiencing grief and loss. Provides support, information, and resources.',
    website: 'https://griefline.org.au/',
    phone: '1300 845 745',
    category: 'death-management',
    icon: <Heart size={24} />,
    color: 'bg-purple-50',
    textColor: 'text-purple-600',
    additionalInfo: 'Griefline provides free telephone and online counselling services for anyone experiencing grief and loss, regardless of how long ago the loss occurred.'
  },
  {
    id: 'compassionate-friends',
    name: 'The Compassionate Friends',
    description: 'Support organization for bereaved parents, siblings, and grandparents. Provides peer support and understanding for those who have lost a child.',
    website: 'https://www.compassionatefriends.org.au/',
    category: 'death-management',
    icon: <Users size={24} />,
    color: 'bg-blue-50',
    textColor: 'text-blue-600',
    additionalInfo: 'The Compassionate Friends provides support to bereaved parents, siblings, and grandparents following the death of a child of any age.'
  },
  {
    id: 'bereavement-care',
    name: 'Bereavement Care Centre',
    description: 'Provides support, counselling, and resources for people experiencing grief and loss. Offers individual and group support services.',
    website: 'https://www.bereavementcare.com.au/',
    category: 'death-management',
    icon: <Heart size={24} />,
    color: 'bg-pink-50',
    textColor: 'text-pink-600',
    additionalInfo: 'The Bereavement Care Centre provides professional support and counselling services for individuals and families experiencing grief and loss.'
  },
  {
    id: 'public-trustee',
    name: 'Public Trustee',
    description: 'Government service providing will writing, estate administration, and executor services. Available in each state and territory.',
    website: 'https://www.pt.qld.gov.au/',
    category: 'death-management',
    icon: <FileText size={24} />,
    color: 'bg-indigo-50',
    textColor: 'text-indigo-600',
    additionalInfo: 'Public Trustees in each state and territory provide will writing, estate administration, and executor services. Contact your local Public Trustee for information.'
  },
  // Professional Services
  {
    id: 'aps',
    name: 'Australian Psychological Society',
    description: 'Professional body for psychologists in Australia. Provides a directory to help you find a registered psychologist in your area.',
    website: 'https://www.psychology.org.au/',
    phone: '1800 333 497',
    category: 'professional',
    icon: <Brain size={24} />,
    color: 'bg-green-50',
    textColor: 'text-green-600',
    additionalInfo: 'The APS is the largest professional association for psychologists in Australia. Their Find a Psychologist service helps you locate registered psychologists.'
  },
  {
    id: 'aca',
    name: 'Australian Counselling Association',
    description: 'Professional association for counsellors and psychotherapists. Provides a directory to find qualified counsellors in your area.',
    website: 'https://www.theaca.net.au/',
    category: 'professional',
    icon: <Users size={24} />,
    color: 'bg-blue-50',
    textColor: 'text-blue-600',
    additionalInfo: 'The ACA is the largest professional association for counsellors and psychotherapists in Australia. They maintain a directory of qualified practitioners.'
  },
  {
    id: 'black-dog',
    name: 'Black Dog Institute',
    description: 'Medical research institute focusing on mental health. Provides information, resources, and online tools for mental health and wellbeing.',
    website: 'https://www.blackdoginstitute.org.au/',
    category: 'professional',
    icon: <Brain size={24} />,
    color: 'bg-gray-50',
    textColor: 'text-gray-600',
    additionalInfo: 'The Black Dog Institute is a medical research institute that investigates mental health across the lifespan, with a focus on mood disorders.'
  },
  {
    id: 'sane',
    name: 'SANE Australia',
    description: 'National mental health charity providing support, information, and advocacy for people affected by complex mental health issues.',
    website: 'https://www.sane.org/',
    phone: '1800 187 263',
    category: 'support',
    icon: <LifeBuoy size={24} />,
    color: 'bg-purple-50',
    textColor: 'text-purple-600',
    additionalInfo: 'SANE Australia is a national mental health charity working to support people affected by complex mental health issues through support, information, and advocacy.'
  }
];

const categoryLabels: Record<Resource['category'], string> = {
  'life-management': 'Life Management',
  'death-management': 'Death Management',
  'support': 'Support Services',
  'crisis': 'Crisis Support',
  'professional': 'Professional Services'
};

export function Resources() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Resource['category'] | 'all'>('all');
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleCall = (phone: string) => {
    window.location.href = `tel:${phone}`;
  };

  const handleVisitWebsite = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const categories: Array<Resource['category'] | 'all'> = ['all', 'crisis', 'support', 'life-management', 'death-management', 'professional'];

  return (
    <div className="min-h-screen bg-white p-6 md:p-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto mb-8"
      >
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-lg bg-idecide-primary/10 flex items-center justify-center">
            <BookOpen size={32} className="text-idecide-primary" />
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Wellbeing Resources</h1>
            <p className="text-gray-600 mt-1">Access mental health, life management, and support resources</p>
          </div>
        </div>
      </motion.div>

      {/* Search and Filter */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === category
                    ? 'bg-idecide-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category === 'all' ? 'All' : categoryLabels[category]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Resources Grid */}
      <div className="max-w-7xl mx-auto">
        {filteredResources.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No resources found matching your search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource, index) => (
              <motion.div
                key={resource.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="h-full"
              >
                <div className="group h-full bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 flex flex-col cursor-pointer"
                     onClick={() => setSelectedResource(resource)}>
                  <div className={`w-12 h-12 rounded-lg ${resource.color} flex items-center justify-center mb-4`}>
                    <div className={resource.textColor}>
                      {resource.icon}
                    </div>
                  </div>
                  <div className="mb-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {categoryLabels[resource.category]}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-idecide-primary transition-colors">
                    {resource.name}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3 flex-grow">
                    {resource.description}
                  </p>
                  
                  <div className="space-y-2 text-sm text-gray-700 mt-auto pt-4 border-t border-gray-100">
                    {resource.phone && (
                      <p className="flex items-center gap-2">
                        <Phone size={16} className="text-gray-500" />
                        <span className="font-medium text-gray-800">{resource.phone}</span>
                      </p>
                    )}
                    <p className="flex items-center gap-2">
                      <Globe size={16} className="text-gray-500" />
                      <span className="text-gray-600 truncate">{resource.website.replace(/^https?:\/\//, '')}</span>
                    </p>
                  </div>

                  <button className="flex items-center justify-center gap-2 bg-idecide-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-idecide-primary-hover transition-colors mt-4">
                    View Details <ChevronRight size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Resource Detail Modal */}
      <Modal
        isOpen={!!selectedResource}
        onClose={() => setSelectedResource(null)}
        title={selectedResource?.name || ''}
        maxWidth="xl"
      >
        {selectedResource && (
          <div className="space-y-6">
            {/* Category Badge */}
            <div>
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                {categoryLabels[selectedResource.category]}
              </span>
            </div>

            {/* Description */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Info size={16} />
                Description
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {selectedResource.description}
              </p>
            </div>

            {/* Additional Info */}
            {selectedResource.additionalInfo && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Info size={16} />
                  Additional Information
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {selectedResource.additionalInfo}
                </p>
              </div>
            )}

            {/* Contact Information */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              {selectedResource.phone && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Phone size={16} />
                    Phone Number
                  </h3>
                  <div className="flex items-center gap-3">
                    <p className="text-lg font-medium text-gray-900">{selectedResource.phone}</p>
                    <button
                      onClick={() => handleCall(selectedResource.phone!)}
                      className="flex items-center gap-2 bg-idecide-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-idecide-primary-hover transition-colors"
                    >
                      <Phone size={16} />
                      Call Now
                    </button>
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Globe size={16} />
                  Website
                </h3>
                <div className="flex items-center gap-3">
                  <p className="text-gray-600 break-all">{selectedResource.website}</p>
                  <button
                    onClick={() => handleVisitWebsite(selectedResource.website)}
                    className="flex items-center gap-2 bg-idecide-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-idecide-primary-hover transition-colors whitespace-nowrap"
                  >
                    <ExternalLink size={16} />
                    Visit Website
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
